import {
  useEffect,
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo
} from 'react';
import { useUser } from './user-context';
import { usePostHog } from 'posthog-js/react';

const FeatureFlagContext = createContext();

export const FeatureFlagProvider = ({ children }) => {
  const { user } = useUser();
  const posthogClient = usePostHog();
  const [flags, setFlags] = useState({});

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({ flags }), [flags]);

  // Memoize feature flag handler
  const handleFeatureFlags = useCallback(
    (featureFlags) => {
      const newFlags = featureFlags.reduce((acc, flag) => {
        acc[flag] = posthogClient.isFeatureEnabled(flag);
        return acc;
      }, {});
      setFlags(newFlags);
    },
    [posthogClient]
  );

  useEffect(() => {
    if (!posthogClient) return;
    if (user.email) {
      posthogClient?.identify(user.email, {
        name: user.name,
        email: user.email,
        vip_id: user.id,
        vip_all_roles: user.allRoles,
        vip_groups: user.groups
      });
    } else {
      posthogClient?.reset();
    }
    posthogClient?.reloadFeatureFlags();
  }, [user, posthogClient]);

  useEffect(() => {
    if (!posthogClient) return;
    posthogClient?.onFeatureFlags(handleFeatureFlags);
  }, [posthogClient, handleFeatureFlags]);

  return (
    <FeatureFlagContext.Provider value={contextValue}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

export const useIsFeatureFlagEnabled = (flagKey) => {
  const { flags } = useContext(FeatureFlagContext);
  return flags[flagKey] ?? false;
};
